import React, { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useAlert } from '../../../context/alert';
import { ModeratorPermission, CreateModeratorRequest, MetricPermissionModel } from '../../../types/api/moderators';
import { MetricType } from '../../../types/api/metrics/metricsType';
import { createModerator } from '../../../services/moderators';
import { createPermissionsFromSetDynamic } from '../ModerationList+Data';
import ModalSheet from '../../basic/ModalSheet';
import { ModeratorPermissionsModal } from './ModeratorPermissionsModal';
import { Stack, Button, TextInput, Text, FormControl } from '@primer/react'

interface ModeratorModalProps {
    isOpen: boolean;
    onDidSaved: () => void;
    onClose: () => void;
}

interface ValidationErrors {
    name?: string[];
    login?: string[];
    password?: string[];
}

const EMPTY_NUMBERS_ARRAY: number[] = [];
const EMPTY_STRINGS_ARRAY: string[] = [];
const EMPTY_SET = new Set<ModeratorPermission>();

export const ModeratorModal: React.FC<ModeratorModalProps> = ({ isOpen, onDidSaved, onClose }) => {

    const [selectedPermissions, setSelectedPermissions] = useState<Set<ModeratorPermission>>(EMPTY_SET);
    const [selectedMetricPermissions, setSelectedMetricPermissions] = useState<MetricPermissionModel>(
        Object.values(MetricType).reduce((acc, type) => ({
            ...acc,
            [type]: false
        }), {} as MetricPermissionModel)
    );

    const [name, setName] = useState('');
    const [login, setLogin] = useState('');
    const [password, setPassword] = useState('');
    const [errors, setErrors] = useState<ValidationErrors>({});

    const [isPending, setIsPending] = useState<boolean>(false)
    const [hasValidationAttempt, setHasValidationAttempt] = useState(false);
    const { setSuccess, setError, setInfo } = useAlert();

    const { mutate: createModeratorRequest } = useMutation({
        mutationFn: (data: CreateModeratorRequest) => createModerator(data),
        onSuccess: (data) => {
            setIsPending(false);
            onDidSaved()
            onClose()
            setInfo('Модератор успешно создан')
        },
        onError: (error) => {
            setError(error.message)
            setIsPending(false)
        }
    });

    const saveModerator = () => {
        setIsPending(true)
        const permissions = createPermissionsFromSetDynamic(selectedPermissions, selectedMetricPermissions)

        createModeratorRequest({
            username: login,
            password: password,
            name: name,
            permissions: permissions
        })
    }

    // Валидация наименования
    const validateName = (value: string): string[] | undefined => {
        if (!value.trim()) {
            return ['Наименование обязательно для заполнения'];
        }

        const errors: string[] = [];

        // Проверка длины
        if (value.length > 100) {
            errors.push('Максимальное количество символов - 100');
        }

        return errors.length > 0 ? errors : undefined;
    };

    // Валидация логина
    const validateLogin = (value: string): string[] | undefined => {
        if (!value.trim()) {
            return ['Логин обязателен для заполнения'];
        }

        const errors: string[] = [];

        // Проверка длины
        if (value.length < 6) {
            errors.push('Минимальное количество символов - 6');
        }
        if (value.length > 20) {
            errors.push('Максимальное количество символов - 20');
        }

        // Проверка начала с буквы
        if (!/^[a-zA-Z]/.test(value)) {
            errors.push('Логин должен начинаться с буквы');
        }

        // Проверка допустимых символов
        if (!/^[a-zA-Z][a-zA-Z0-9._-]*$/.test(value)) {
            errors.push('Можно использовать только латинские буквы, цифры, тире (-), подчеркивания (_) и точки (.)');
        }

        // Проверка на @
        if (value.includes('@')) {
            errors.push('Знак @ в логине недопустим');
        }

        // Проверка на пробелы
        if (value.includes(' ')) {
            errors.push('Пробел в логине недопустим');
        }

        // Проверка окончания на точку
        if (value.endsWith('.')) {
            errors.push('Логин не может заканчиваться точкой');
        }

        return errors.length > 0 ? errors : undefined;
    };

    // Валидация пароля
    const validatePassword = (value: string): string[] | undefined => {
        if (!value) {
            return ['Пароль обязателен для заполнения'];
        }

        const errors: string[] = [];

        // Проверка длины
        if (value.length < 8) {
            errors.push('Минимальное количество символов - 8');
        }
        if (value.length > 14) {
            errors.push('Максимальное количество символов - 14');
        }

        // Проверка на только латинские буквы и цифры
        if (!/^[a-zA-Z0-9]+$/.test(value)) {
            errors.push('Пароль должен состоять из букв латинского алфавита');
        }

        // Проверка на наличие строчных и прописных букв
        const hasLowerCase = /[a-z]/.test(value);
        const hasUpperCase = /[A-Z]/.test(value);

        if (!hasLowerCase || !hasUpperCase) {
            errors.push('Пароль должен содержать строчные и заглавные буквы');
        }

        return errors.length > 0 ? errors : undefined;
    };
    // Обновление ошибок при изменении полей
    useEffect(() => {
        if (!hasValidationAttempt) return;
        const newErrors: ValidationErrors = {};

        const nameError = validateName(name);
        if (nameError) newErrors.name = nameError;

        const loginError = validateLogin(login);
        if (loginError) newErrors.login = loginError;

        const passwordError = validatePassword(password);
        if (passwordError) newErrors.password = passwordError;

        setErrors(newErrors);
    }, [name, login, password]);

    // Проверка валидности формы
    const isFormValid = () => {
        return Object.keys(errors).length === 0 && name.trim() && login.trim() && password && selectedPermissions.size > 0;
    };

    const savePermissions = (list: Set<ModeratorPermission>, metric: MetricPermissionModel) => {
        setSelectedPermissions(list)
        setSelectedMetricPermissions(metric)
    }

    const trySaveModerator = () => {
        if (!isFormValid()) return;
        saveModerator()
    };

    return (
        <ModalSheet
            isOpen={isOpen}
            onClose={onClose}
            title="Создать учетную запись модератора"
            size="middle"
            headerSize="regular"
        >
            {({ setTitle }) => (
                <Stack className='mt-[16px]'>
                    <FormControl>
                        <FormControl.Label className='mb-1'>Наименование</FormControl.Label>
                        <TextInput
                            type="text"
                            inputMode="text"
                            value={name}
                            onChange={(e) => {
                                setName(e.target.value)
                                setHasValidationAttempt(true)
                            }}
                            maxLength={100}
                            placeholder="Придумайте наименование модератора"
                            size="medium"
                            className='w-full'
                            contrast={true}
                            validationStatus={errors.name && hasValidationAttempt ? 'error' : undefined}
                        />
                        {errors.name && hasValidationAttempt && (
                            <FormControl.Validation variant="error">
                                {errors.name}
                            </FormControl.Validation>
                        )}
                    </FormControl>

                    <FormControl>
                        <FormControl.Label className='mb-1'>Логин</FormControl.Label>
                        <TextInput
                            type="text"
                            inputMode="text"
                            value={login}
                            onChange={(e) => {
                                setLogin(e.target.value)
                                setHasValidationAttempt(true)
                            }}
                            maxLength={20}
                            placeholder="Введите логин модератора"
                            size="medium"
                            className='w-full'
                            autoComplete='false'
                            contrast={true}
                            validationStatus={errors.login && hasValidationAttempt ? 'error' : undefined}
                        />
                        {errors.login && errors.login.length > 0 && hasValidationAttempt && (
                            <Stack gap="condensed">
                                {errors.login.map((error, index) => (
                                    <FormControl.Validation variant="error">
                                        <div key={index}>{error}</div>
                                    </FormControl.Validation>
                                ))}
                            </Stack>
                        )}
                    </FormControl>

                    <FormControl>
                        <FormControl.Label className='mb-1'>Пароль</FormControl.Label>
                        <TextInput
                            type="text"
                            value={password}
                            onChange={(e) => {
                                setPassword(e.target.value)
                                setHasValidationAttempt(true)
                            }}
                            maxLength={14}
                            placeholder="Введите пароль модератора"
                            size="medium"
                            className='w-full'
                            contrast={true}
                            autoComplete="nope"
                            validationStatus={errors.password && hasValidationAttempt ? 'error' : undefined}
                        />
                        {errors.password && hasValidationAttempt && (
                            <Stack gap="condensed">
                                {errors.password.map((error, index) => (
                                    <FormControl.Validation variant="error">
                                        <div key={index}>{error}</div>
                                    </FormControl.Validation>
                                ))}
                            </Stack>
                        )}
                    </FormControl>

                    <Text as="span" weight='semibold' className='text-[14px] text-aloix-black'>
                        Доступы
                    </Text>
                    <ModeratorPermissionsModal
                        permissions={selectedPermissions}
                        metricsPermissions={selectedMetricPermissions}
                        didPermissionsSelected={savePermissions}
                    />

                    <Stack direction="horizontal" gap='normal' justify='end'>
                        <div></div>
                        <Button
                            onClick={onClose}
                            size='small'
                            variant="default">
                            Отмена
                        </Button>
                        <Button
                            onClick={trySaveModerator}
                            size='small'
                            variant="primary"
                            loading={isPending}
                            disabled={isPending || !isFormValid()}>
                            Сохранить
                        </Button>
                    </Stack>
                </Stack >
            )}
        </ModalSheet >
    );
};